import type { Evidence, NewsPayload } from './api';

/**
 * Coverage notes versus open questions.
 *
 * From producer 0.2.0 on, `coverage` says per source how much text was read and
 * `unknowns` holds only open questions about the story. Earlier versions (all
 * cards published before) carried the coverage notes inside `unknowns`, in
 * English. Those are recognised here and shown as coverage, in German, with the
 * original wording kept for the technical disclosure. Nearly every card has some
 * coverage limitation, so the card surface shows it only as a compact hint.
 */

export interface CoverageNote {
  source_id: string;
  /** Reader-facing German note. */
  note: string;
  /** Technical diagnosis, for the disclosure only. */
  detail?: string;
  /** Short form for the card surface. */
  short: string;
}

const LEGACY =
  /^([a-z0-9-]+): (only (the feed excerpt|the headline|the full text) was available|extracted body|full text not obtained|the article body could not be retrieved|excerpt only|headline only)/;

function legacyNote(kind: string): { note: string; short: string } {
  if (kind.startsWith('only the feed excerpt')) return { note: 'Nur der Anriss aus dem Feed war verfügbar.', short: 'Nur Anriss' };
  if (kind.startsWith('only the headline')) return { note: 'Nur der Titel war verfügbar.', short: 'Nur Titel' };
  if (kind.startsWith('only the full text')) return { note: 'Nur der Volltext war verfügbar.', short: 'Volltext' };
  if (kind.startsWith('extracted body')) return { note: 'Der gelesene Text war zu kurz für den ganzen Artikel.', short: 'Text unvollständig' };
  if (kind.startsWith('headline only')) return { note: 'Nur der Titel war verfügbar.', short: 'Nur Titel' };
  if (kind.startsWith('excerpt only')) return { note: 'Nur der Anriss aus dem Feed war verfügbar.', short: 'Nur Anriss' };
  if (kind.startsWith('full text not obtained')) return { note: 'Der Volltext konnte nicht gelesen werden.', short: 'Kein Volltext' };
  return { note: 'Der Artikeltext war nicht abrufbar; nur der Anriss lag vor.', short: 'Nur Anriss' };
}

function shortFor(textSource: string): string {
  if (textSource === 'excerpt') return 'Nur Anriss';
  if (textSource === 'none') return 'Nur Titel';
  return 'Volltext';
}

export function classifyUnknowns(p: NewsPayload): { coverage: CoverageNote[]; questions: string[] } {
  const coverage: CoverageNote[] = [];
  const seen = new Set<string>();
  const add = (c: CoverageNote) => {
    const key = `${c.source_id}|${c.note}`;
    if (seen.has(key)) return;
    seen.add(key);
    coverage.push(c);
  };
  for (const c of p.coverage ?? []) {
    if (c.text_source === 'full_text' && !c.note) continue;
    add({ source_id: c.source_id, note: c.note, detail: c.detail, short: shortFor(c.text_source) });
  }
  const questions: string[] = [];
  for (const u of p.unknowns ?? []) {
    const m = LEGACY.exec(u);
    if (m) {
      const { note, short } = legacyNote(m[2]);
      add({ source_id: m[1], note, detail: u, short });
    } else if (!questions.includes(u)) {
      questions.push(u);
    }
  }
  return { coverage, questions };
}

/** The compact card hint: the most limiting coverage, or null when all was read in full. */
export function coverageHint(p: NewsPayload): string | null {
  const { coverage } = classifyUnknowns(p);
  const relevant = coverage.filter((c) => c.short !== 'Volltext');
  if (relevant.length === 0) return null;
  const order = ['Nur Titel', 'Kein Volltext', 'Nur Anriss', 'Text unvollständig'];
  relevant.sort((a, b) => order.indexOf(a.short) - order.indexOf(b.short));
  return relevant[0].short;
}

export interface EvidenceItem {
  /** The newest revision of this source item. */
  latest: Evidence;
  /** Earlier revisions of the same item, newest first. */
  older: Evidence[];
}

/**
 * Legacy versions repeat the same source item once per revision. Group by item:
 * show the newest revision, keep the older ones for the disclosure. `fetchedAt`
 * gives a revision's fetch time where known; otherwise array order decides
 * (later entries are newer).
 */
export function groupEvidence(evidence: Evidence[], fetchedAt: (revisionId: string) => string | undefined): EvidenceItem[] {
  const order: string[] = [];
  const byItem = new Map<string, Array<{ e: Evidence; idx: number }>>();
  evidence.forEach((e, idx) => {
    const key = e.item_id || e.revision_id;
    if (!byItem.has(key)) {
      byItem.set(key, []);
      order.push(key);
    }
    byItem.get(key)!.push({ e, idx });
  });
  return order.map((key) => {
    const revs = byItem.get(key)!;
    revs.sort((a, b) => {
      const ta = Date.parse(fetchedAt(a.e.revision_id) ?? '') || 0;
      const tb = Date.parse(fetchedAt(b.e.revision_id) ?? '') || 0;
      return tb - ta || b.idx - a.idx;
    });
    return { latest: revs[0].e, older: revs.slice(1).map((r) => r.e) };
  });
}

export function distinctSources(evidence: Evidence[]): number {
  return new Set(evidence.map((e) => e.source_id)).size;
}
